/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  cellValueFormatter,
  code3Formatter,
  createDefaultGridModel,
  ROW_DELETED_CLASS,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { getRouteInitData, whenModified } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_ROW_LAYOUT } from '@gh/core-ui';
import { GridDefinition, GridDefinitionFactory, GridLayout } from '@gh/core-ui-grid';
import {
  getReportRohcMasterCcId,
  getReportRohcOfficeCcId,
  ReportRohcMasterCc,
  ReportRohcOfficeCc,
} from '../../../resources/report-roch-details.model';
import { ReportRohcDetailsBusinessService } from '../../../resources/report-rohc-details-business.service';
import { ReportRohcDetailsCommandService } from '../report-rohc-details-command.service';
import { ReportRohcDetailsFormState } from '../report-rohc-details-form.service';

@Component({
  selector: 'gh-report-rohc-cc-management',
  template: require('./report-rohc-cc-management.component.html'),
  styles: [STYLES_FLEX_ROW_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportRohcCcManagementComponent implements OnInit {
  masterCcGridDefinition: GridDefinition;
  officeCcGridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              public form: ReportRohcDetailsFormState,
              private commands: ReportRohcDetailsCommandService,
              private business: ReportRohcDetailsBusinessService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.masterCcGridDefinition = this.buildMasterCcGridDefinition();
    this.officeCcGridDefinition = this.buildOfficeCcGridDefinition();
  }

  onMasterCcCreate(): void {
    this.commands.createMasterCc();
  }

  onMasterCcOpen(masterCc: ReportRohcMasterCc): void {
    this.commands.openMasterCc(masterCc);
  }

  onMasterCcDelete(masterCc: ReportRohcMasterCc): void {
    this.commands.deleteMasterCc(masterCc);
  }

  onSelectedMasterCcChange(id: ID): void {
    this.commands.selectMasterCc(id);
  }

  onOfficeCcAdd(): void {
    this.commands.addOfficeCc();
  }

  onOfficeCcRemove(officeCc: ReportRohcOfficeCc): void {
    this.commands.removeOfficeCc(officeCc);
  }

  onLayoutChange(layout: GridLayout): void {
    this.commands.setGridLayout('masterCc', layout);
  }

  private buildMasterCcGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({ getId: getReportRohcMasterCcId });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [
        {
          field: 'code',
          headerName: this.translations.get('lbl_number'),
          width: 150,
          suppressSizeToFit: true,
          valueFormatter: code3Formatter,
        },
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 150 },
      ],
      options: {
        rowSelection: 'single',
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: ReportRohcMasterCc }) => whenModified(data),
        },
      },
    });
  }

  private buildOfficeCcGridDefinition(): GridDefinition {
    const { officeCcGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({ getId: getReportRohcOfficeCcId });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      layout: officeCcGridLayout,
      model,
      columns: {
        code: { valueFormatter: code3Formatter },
      },
      options: {
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: ReportRohcOfficeCc }) => !data.isActive,
          [ROW_DIRTY_CLASS]: ({ data }: { data: ReportRohcOfficeCc }) => whenModified(data),
        },
      },
    });
  }
}
